import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Strips '+91' or '91' country prefix if present so that standard 10-digit Indian
 * phone numbers display cleanly as 10 digits across all UI screens.
 * Other international prefixes are preserved.
 */
export function formatPhone(phone) {
  if (!phone) return '-';
  const str = String(phone).trim();
  if (str.startsWith('+91')) {
    return str.slice(3).trim();
  }
  if (str.startsWith('91') && str.length === 12) {
    return str.slice(2).trim();
  }
  return str;
}

/**
 * Safely determines patient age from explicit age or dateOfBirth string.
 * Avoids 'NaN yrs' when age is given or DOB is missing.
 */
export function formatPatientAge(age, dateOfBirth) {
  if (age !== undefined && age !== null && age !== '' && !isNaN(Number(age))) {
    return `${Number(age)} yrs`;
  }
  if (dateOfBirth) {
    const birthDate = new Date(dateOfBirth);
    if (!isNaN(birthDate.getTime())) {
      const today = new Date();
      let calculatedAge = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        calculatedAge--;
      }
      if (calculatedAge >= 0) {
        return `${calculatedAge} yrs`;
      }
    }
  }
  return '-';
}

/**
 * Extracts raw name string from a doctor object or string.
 */
function extractDoctorRawName(doctorOrName) {
  if (!doctorOrName) return '';
  if (typeof doctorOrName === 'string') return doctorOrName.trim();
  if (typeof doctorOrName === 'object') {
    const doc = doctorOrName.doctor || doctorOrName;
    const firstName = (doc.firstName || doc.user?.firstName || '').trim();
    const lastName = (doc.lastName || doc.user?.lastName || '').trim();
    const combinedFirstLast = `${firstName} ${lastName}`.trim();
    if (combinedFirstLast) return combinedFirstLast;

    const nameStr = doc.name || doc.doctorName || doc.fullName || doc.username || '';
    if (nameStr) return String(nameStr).trim();
  }
  return '';
}

/**
 * Strips any recurring prefix forms of Dr. / Doctor / etc.
 */
function cleanDoctorTitlePrefix(rawName) {
  let clean = (rawName || '').trim();
  while (/^(dr\b\.?|doctor\b\.?)\s*/i.test(clean)) {
    clean = clean.replace(/^(dr\b\.?|doctor\b\.?)\s*/i, '').trim();
  }
  return clean;
}

/**
 * Universal formatter for doctor names.
 * Ensures consistent output format: "Dr. FirstName LastName" (Title Case),
 * completely preventing duplicate titles like "Dr. Dr. ..." or empty names like "Dr. ".
 *
 * @param {string|object} doctorOrName - Doctor object or string name
 * @param {string} [fallback='Dr. Consultant'] - Fallback name if none available
 * @returns {string} - Clean formatted doctor name
 */
export function formatDoctorName(doctorOrName, fallback = 'Dr. Consultant') {
  const raw = extractDoctorRawName(doctorOrName);
  const clean = cleanDoctorTitlePrefix(raw);

  if (!clean || clean.toLowerCase() === 'consultant' || clean.toLowerCase() === 'doctor') {
    return fallback;
  }

  // Split words and apply proper title casing
  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length === 0) return fallback;

  const formattedWords = words.map(word => {
    // Preserve acronyms if already uppercase (like MD, MBBS, JR, II, III)
    if (word.length > 1 && word === word.toUpperCase() && !/^[A-Z][a-z]+$/.test(word)) {
      return word;
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return `Dr. ${formattedWords.join(' ')}`;
}

/**
 * Extracts initials for a doctor avatar, ignoring the "Dr." title.
 * e.g., "Dr. Priya Sharma" -> "PS"
 *       "Dr. Dobby" -> "DO"
 *       "Dr. Dobby Jr" -> "DJ"
 *
 * @param {string|object} doctorOrName - Doctor object or string name
 * @param {string} [fallback='DR'] - Fallback initials
 * @returns {string} - Up to 2 uppercase initials
 */
export function getDoctorInitials(doctorOrName, fallback = 'DR') {
  const raw = extractDoctorRawName(doctorOrName);
  const clean = cleanDoctorTitlePrefix(raw);

  if (!clean || clean.toLowerCase() === 'consultant' || clean.toLowerCase() === 'doctor') {
    return fallback;
  }

  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length === 0) return fallback;

  if (words.length === 1) {
    const single = words[0];
    return single.slice(0, 2).toUpperCase();
  }

  const firstInitial = words[0][0];
  const lastInitial = words[words.length - 1][0];
  return `${firstInitial}${lastInitial}`.toUpperCase();
}

